import { Trans } from '@lingui/react';
import { Typography } from '@startuptools/ui/Typography';
import { Card } from '@startuptools/ui/Card';
import { Stack } from '@startuptools/ui/Stack';
import { Avatar } from '@startuptools/ui/Avatar';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useWarrantProgramSuspenseQuery } from '../WarrantProgram.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Link, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { QesoProgramStepper } from './components/ProgramStepper';
import { OptionsRegistrations } from '../common/components/main-steps/OptionsRegistrations';
import { OptionsDocumentsPage } from '../common/components/main-steps/options-documents/page';
import { useNavigate } from '../../../components/Navigate';
import {
  wpEditDecisionLinkOld,
  wpEditDocumentsLink,
  wpEditLanguageLink,
  wpEditLink,
  wpEditOptionsSubsLink,
} from '../../../components/Links';
import { OptionsDecisionStepper } from '../common/components/main-steps/DecisionStepper';

export const QesoEditRoutes = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const { pathname } = useNavigate();

  if (pathname.endsWith('edit')) {
    return <Navigate to="main-step-program" replace />;
  }

  return (
    <Routes>
      <Route path="" element={<QesoEdit warrantProgramId={warrantProgramId} />}>
        <Route path="main-step-program" element={<QesoProgramStepper warrantProgramId={warrantProgramId} />}>
          <Route path="*" element={<QesoProgramStepper warrantProgramId={warrantProgramId} />} />
        </Route>
        <Route
          path="main-step-options-subscriptions"
          element={<OptionsRegistrations warrantProgramId={warrantProgramId} />}
        >
          <Route path="*" element={<OptionsRegistrations warrantProgramId={warrantProgramId} />} />
        </Route>
        <Route path="main-step-decision" element={<OptionsDecisionStepper warrantProgramId={warrantProgramId} />}>
          <Route path="*" element={<OptionsDecisionStepper warrantProgramId={warrantProgramId} />} />
        </Route>
        <Route
          path="main-step-options-documents"
          element={<OptionsDocumentsPage warrantProgramId={warrantProgramId} navigateToDecisionStep={() => null} />}
        >
          <Route
            path="*"
            element={<OptionsDocumentsPage warrantProgramId={warrantProgramId} navigateToDecisionStep={() => null} />}
          />
        </Route>
      </Route>
    </Routes>
  );
};

const QesoEdit = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const { link, isActive } = useNavigate();
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId, getQesoErrors: true } });

  const basePath = wpEditLink(warrantProgram);

  return (
    <Stack gap={3}>
      <Typography level="h3">
        <Trans
          id='Qualified Employee Stock Options - {name} - "{description}"'
          values={{ name: warrantProgram.name, description: warrantProgram.description }}
        />
      </Typography>

      <Card>
        <Stack direction="row" gap={3} alignItems="center">
          <Stack
            component={Link}
            to={link(wpEditLanguageLink(warrantProgram))}
            direction="row"
            gap={1}
            alignItems="center"
            sx={{ cursor: 'pointer' }}
          >
            <Avatar
              size="sm"
              variant={isActive(`${basePath}/main-step-program`) ? 'solid' : 'soft'}
              color={isActive(`${basePath}/main-step-program`) ? 'primary' : 'neutral'}
            >
              1
            </Avatar>
            <Typography level="title-lg">
              <Trans id="The Program" />
            </Typography>
          </Stack>

          <ArrowForwardIosIcon />

          <Stack
            component={Link}
            to={link(wpEditOptionsSubsLink(warrantProgram))}
            direction="row"
            gap={1}
            alignItems="center"
            sx={{ cursor: 'pointer' }}
          >
            <Avatar
              size="sm"
              variant={isActive(`${basePath}/main-step-options-subscriptions`) ? 'solid' : 'soft'}
              color={isActive(`${basePath}/main-step-options-subscriptions`) ? 'primary' : 'neutral'}
            >
              2
            </Avatar>
            <Typography level="title-lg">
              <Trans id="Option Subscription" />
            </Typography>
          </Stack>

          <ArrowForwardIosIcon />

          <Stack
            component={Link}
            to={link(wpEditDecisionLinkOld(warrantProgram))}
            direction="row"
            gap={1}
            alignItems="center"
            sx={{ cursor: 'pointer' }}
          >
            <Avatar
              size="sm"
              variant={isActive(`${basePath}/main-step-decision`) ? 'solid' : 'soft'}
              color={isActive(`${basePath}/main-step-decision`) ? 'primary' : 'neutral'}
            >
              3
            </Avatar>
            <Typography level="title-lg">
              <Trans id="Decision" />
            </Typography>
          </Stack>

          <ArrowForwardIosIcon />

          <Stack
            component={Link}
            to={link(wpEditDocumentsLink(warrantProgram))}
            direction="row"
            gap={1}
            alignItems="center"
            sx={{ cursor: 'pointer' }}
          >
            <Avatar
              size="sm"
              variant={isActive(`${basePath}/main-step-options-documents`) ? 'solid' : 'soft'}
              color={isActive(`${basePath}/main-step-options-documents`) ? 'primary' : 'neutral'}
            >
              4
            </Avatar>
            <Typography level="title-lg">
              <Trans id="Documents" />
            </Typography>
          </Stack>
        </Stack>
      </Card>
      <Outlet />
    </Stack>
  );
};
